import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { mainSiteUrl } from '@/mocks/categories';
import { ArticleJsonLd } from '@/components/base/SeoJsonLd';

export default function BeginnerTipsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://kakobuypolska.sbs';
  const pageUrl = `${siteUrl}/kakobuy-porady-dla-poczatkujacych`;

  return (
    <main className="min-h-screen bg-background-50">
      <ArticleJsonLd
        url={pageUrl}
        headline="Kakobuy dla Początkujących — 10 Najlepszych Porad na Start"
        description="Zaczynasz swoją przygodę z Kakobuy i arkuszami spreadsheet? Oto kompletny zestaw wskazówek, które pomogą Ci uniknąć błędów początkującego i zacząć świadomie kupować produkty z Chin."
        imageUrl="https://readdy.ai/api/search-image?query=A%20warm%20inviting%20workspace%20with%20an%20open%20notebook%20filled%20with%20handwritten%20notes%20and%20a%20pencil%20beside%20it%20on%20a%20light%20cream%20desk%20surface%20a%20small%20potted%20plant%20in%20the%20corner%20soft%20golden%20morning%20light%20creating%20a%20gentle%20inspiring%20atmosphere%20Scandinavian%20home%20office%20aesthetic%20welcoming%20and%20educational%20with%20plenty%20of%20clean%20space%20for%20heading%20text%20warm%20neutral%20beige%20tones&width=1200&height=600&seq=article-beginner-hero&orientation=landscape"
        datePublished="2026-07-20"
        dateModified="2026-07-21"
        authorName="Kakobuy Polska"
      />
      <header className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 text-foreground-950 hover:opacity-80 transition-opacity cursor-pointer whitespace-nowrap">
              <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
                <span className="text-background-50 font-bold text-sm">K</span>
              </div>
              <span className="font-semibold text-base tracking-tight">Kakobuy<span className="text-primary-500">Polska</span></span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer">
                <i className="ri-arrow-left-line mr-1"></i>Strona główna
              </Link>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="px-5 py-2 text-sm font-semibold bg-primary-500 text-background-50 dark:text-foreground-950 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                Kakobuy Spreadsheet
              </a>
            </div>
          </div>
        </div>
      </header>

      <article className="py-10 md:py-16">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="mb-8 md:mb-10 rounded-2xl overflow-hidden">
            <img src="https://readdy.ai/api/search-image?query=A%20warm%20inviting%20workspace%20with%20an%20open%20notebook%20filled%20with%20handwritten%20notes%20and%20a%20pencil%20beside%20it%20on%20a%20light%20cream%20desk%20surface%20a%20small%20potted%20plant%20in%20the%20corner%20soft%20golden%20morning%20light%20creating%20a%20gentle%20inspiring%20atmosphere%20Scandinavian%20home%20office%20aesthetic%20welcoming%20and%20educational%20with%20plenty%20of%20clean%20space%20for%20heading%20text%20warm%20neutral%20beige%20tones&width=1200&height=600&seq=article-beginner-hero&orientation=landscape" alt="Kakobuy dla początkujących - porady na start" title="Kakobuy poradnik dla początkujących kupujących" className="w-full h-48 md:h-72 object-cover object-top" />
          </div>

          <header className="mb-10 md:mb-14">
            <div className="flex items-center gap-3 text-xs md:text-sm text-foreground-500 mb-4">
              <span className="px-2.5 py-1 bg-green-50 text-green-600 rounded-full font-medium">Poradnik</span>
              <span>•</span>
              <span>11 min czytania</span>
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4 md:mb-5">
              Kakobuy dla Początkujących — 10 Najlepszych Porad na Start
            </h1>
            <p className="text-sm md:text-lg text-foreground-600 leading-relaxed">
              Zaczynasz swoją przygodę z Kakobuy i arkuszami spreadsheet? Oto kompletny zestaw wskazówek, które pomogą Ci uniknąć błędów początkującego i zacząć świadomie kupować produkty z Chin.
            </p>
          </header>

          <div className="prose-custom space-y-6 md:space-y-8">
            <section>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">1. Zacznij od Darmowego Arkusza Spreadsheet</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                Nie wydawaj pieniędzy na premium arkusze, dopóki nie zrozumiesz jak działa system. Podstawowy, darmowy <strong>Kakobuy Spreadsheet</strong> zawiera setki zweryfikowanych produktów i w zupełności wystarczy na początek. Znajdziesz go na naszej <Link to="/" className="text-primary-500 hover:text-primary-600 underline">stronie głównej</Link>. Przejrzyj różne kategorie, zapoznaj się z układem arkusza i zrozum, jakie informacje są w nim zawarte.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">2. Załóż Konto na Kakobuy</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                Zanim zaczniesz przeglądać produkty, załóż konto na oficjalnej platformie Kakobuy. Proces rejestracji jest prosty i darmowy. Pamiętaj, aby używać silnego, unikalnego hasła i — jeśli to możliwe — włączyć uwierzytelnianie dwuskładnikowe. Więcej o bezpieczeństwie konta przeczytasz w naszym <Link to="/poradnik-bezpieczenstwa-kakobuy" className="text-primary-500 hover:text-primary-600 underline">poradniku bezpieczeństwa</Link>.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">3. Zrozum Proces Zakupowy</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                Zakupy przez Kakobuy różnią się od standardowych zakupów online. Proces wygląda następująco: (1) znajdujesz produkt w arkuszu spreadsheet, (2) kopiujesz link i wklejasz go w wyszukiwarce Kakobuy, (3) dodajesz produkt do koszyka i opłacasz zamówienie, (4) produkt jest wysyłany do magazynu Kakobuy w Chinach, (5) zamawiasz zdjęcia QC, (6) po akceptacji, Kakobuy wysyła paczkę do Polski. Cały proces od zamówienia do otrzymania przesyłki trwa zwykle 2-4 tygodnie.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">4-6. Kolejne Kluczowe Wskazówki</h2>
              <ul className="list-disc pl-5 space-y-2 text-sm md:text-base text-foreground-700">
                <li><strong>Zawsze zamawiaj zdjęcia QC</strong> — to Twoje ubezpieczenie przed otrzymaniem wadliwego produktu. Koszt to zwykle tylko kilka juanów.</li>
                <li><strong>Kupuj od sprawdzonych sprzedawców</strong> — wybieraj tych z oceną minimum 4.7 gwiazdki i dużą historią transakcji.</li>
                <li><strong>Zacznij od małego zamówienia</strong> — nie zamawiaj 10 produktów na raz. Kup 1-2 przedmioty, przetestuj proces i dopiero wtedy zwiększaj skalę.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">7-10. Zaawansowane Wskazówki na Start</h2>
              <ul className="list-disc pl-5 space-y-2 text-sm md:text-base text-foreground-700">
                <li><strong>Korzystaj z konsolidacji przesyłek</strong> — połącz kilka produktów w jedną paczkę, aby zaoszczędzić na wysyłce.</li>
                <li><strong>Śledź aktualizacje arkusza</strong> — produkty są regularnie dodawane i usuwane. Sprawdzaj arkusz co tydzień.</li>
                <li><strong>Dołącz do społeczności</strong> — fora i grupy na Reddit, Discord i Telegram to kopalnia wiedzy o Kakobuy.</li>
                <li><strong>Bądź cierpliwy</strong> — dostawa z Chin trwa. Nie panikuj jeśli paczka nie dotrze w 7 dni — to normalne.</li>
              </ul>
            </section>

            <div className="bg-primary-50 border border-primary-200/50 rounded-2xl p-6 md:p-8 text-center mt-10">
              <h3 className="text-lg md:text-xl font-bold text-foreground-950 mb-3">Zacznij Swoją Przygodę z Kakobuy</h3>
              <p className="text-sm md:text-base text-foreground-600 mb-5">Z tą wiedzą jesteś gotowy na pierwsze udane zakupy!</p>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center px-8 py-3.5 bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold text-sm md:text-base rounded-xl hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                Otwórz Kakobuy Spreadsheet<i className="ri-arrow-right-up-line ml-2"></i>
              </a>
            </div>

            <div className="border-t border-background-200/60 pt-8 mt-10">
              <h4 className="text-sm font-semibold text-foreground-700 mb-3">Przydatne linki:</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors">← Powrót do strony głównej Kakobuy Polska</Link></li>
                <li><Link to="/jak-korzystac-z-kakobuy-spreadsheet" className="text-sm text-foreground-500 hover:text-foreground-700 transition-colors">Jak korzystać z Kakobuy Spreadsheet</Link></li>
                <li><Link to="/poradnik-bezpieczenstwa-kakobuy" className="text-sm text-foreground-500 hover:text-foreground-700 transition-colors">Poradnik bezpieczeństwa</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </article>

      <footer className="bg-foreground-950 text-background-50 py-6">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-500">&copy; {new Date().getFullYear()} KakobuyPolska.sbs — Niezależny przewodnik</p>
          <Link to="/" className="text-xs text-foreground-400 hover:text-background-50 transition-colors cursor-pointer">Strona główna</Link>
        </div>
      </footer>
    </main>
  );
}